import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { toast } from "react-toastify";
import LeaveProcessService from "../services/LeaveProcessService";
import { useLoading } from "../Utils/LoadingContext";
import "./LeaveApply.css";

export default function AttendanceDetails() {

    const { setLoading } = useLoading();

    const [activeStartDate, setActiveStartDate] = useState(new Date());
    const [monthSummary, setMonthSummary] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [leaveList, setLeaveList] = useState([]);

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const [leaveType, setLeaveType] = useState("");
    const [leaveUnit, setLeaveUnit] = useState("FULLDAY");
    const [comment, setComment] = useState("");

    const employeeCode = localStorage.getItem("empcode");
    const employeeName = localStorage.getItem("username");
    const today = new Date();

    // ================= LOAD DATA =================

    useEffect(() => {
        fetchMonthSummary(activeStartDate);
        fetchLeaveTypes();
        fetchLeaveList();
    }, [activeStartDate]);

    const getLeaveMMYY = (date) => {
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}${year}`;
    };

    const parseDate = (dateString) => {
        const parts = dateString.split(" ")[0].split("/");
        return new Date(parts[2], parts[0] - 1, parts[1]);
    };

    const formatDate = (date) => {
        return date.toLocaleDateString("en-GB");
    };

    // ================= MONTH SUMMARY =================

    const fetchMonthSummary = async (date) => {

        try {

            setLoading(true);

            const formData = new FormData();
            formData.append("EmployeeCode", employeeCode);
            formData.append("Type", "GetMonthSummary");
            formData.append("LeaveMMYY", getLeaveMMYY(date));

            const response = await LeaveProcessService.getEmployeeCalender(formData);

            if (response.data.statusCode === 200) {
                setMonthSummary(response.data.result || []);
            }

        } catch (error) {

            console.error(error);

        } finally {

            setLoading(false);

        }
    };

    // ================= LEAVE BALANCE =================

    const fetchLeaveTypes = async () => {

        try {

            const formData = new FormData();
            formData.append("EmployeeCode", employeeCode);
            formData.append("Type", "GetLeave");

            const response = await LeaveProcessService.getEmployeeDashBoard(formData);

            if (response.data.statusCode === 200) {
                setLeaveTypes(response.data.result || []);
            }

        } catch (error) {

            console.error(error);
            setLeaveTypes([]);

        }
    };

    // ================= LEAVE LIST =================

    const fetchLeaveList = async () => {

        try {

            const formData = new FormData();
            formData.append("Type", "GetLeaveList");
            formData.append("LeaveType", "GetLeaveType");
            const response = await LeaveProcessService.getEmployeeList(formData);

            if (response.data.statusCode === 200) {
                setLeaveList(response.data.result || []);
            }

        } catch (error) {

            console.error(error);
            setLeaveList([]);

        }
    };

    // ================= STATUS COLORS =================

    const getStatusColor = (date) => {

        const item = monthSummary.find(ms =>
            ms.punchDate &&
            parseDate(ms.punchDate).toDateString() === date.toDateString()
        );

        if (!item) return "";

        switch (item.status) {

           case "PP":
                return "status-pp";
            case "OD":
                return "status-pp";
            case "AR":
                return "status-pp";

            case "AP":
                return "status-ap";

            case "PA":
                return "status-pa";

            case "AA":
                return "status-aa";

            case "WO":
                return "status-wo";
            case "CL":
                return "leave-day";
            case "PL":
                return "leave-day";
            case "ACL":
                return "leave-day";
            case "CLA":
                return "leave-day";

            case "HO":
                return "status-hl";

            default:
                return "status-default";
        }
    };

    // ================= DATE CLICK =================

    const handleDateClick = (date) => {

        if (!fromDate) {
            setFromDate(date);
            setToDate(date);
            return;
        }

        if (fromDate && toDate && fromDate.getTime() === toDate.getTime()) {

            if (date < fromDate) {
                setFromDate(date);
                setToDate(fromDate);
            } else {
                setToDate(date);
            }

            return;
        }

        setFromDate(date);
        setToDate(date);
    };

    // ================= CALCULATE DAYS =================

    let numberOfDays = 0;

    if (fromDate && toDate) {

        const start = new Date(fromDate);
        const end = new Date(toDate);

        const diff = end - start;

        numberOfDays = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
    }

    // ================= VALIDATION =================

    const validateForm = () => {

        if (!fromDate || !toDate) {
            alert("Please select From and To date");
            return false;
        }

        if (fromDate > toDate) {
            alert("From Date cannot be greater than To Date");
            return false;
        }

        if (!leaveUnit) {
            alert("Please select Leave Unit");
            return false;
        }

        if (!leaveType) {
            alert("Please select Leave Type");
            return false;
        }

        if (!comment.trim()) {
            alert("Comment is required");
            return false;
        }

        if (
            leaveType === "2" &&
            (leaveUnit === "FIRST_HALF" || leaveUnit === "SECOND_HALF")
        ) {
            alert("Half Day is not allowed for PL leave");
            return false;
        }

        return true;
    };

    // ================= SUBMIT =================
    // Next Month Last Day
    // API format → yyyy-mm-dd
    const formatDateAPI = (date) => {

        if (!date) return "";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${year}-${month}-${day}`;
    };
    const maxDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    const handleSubmit = async () => {

        try {

            if (!validateForm()) return;

            setLoading(true);

            const formData = new FormData();

            formData.append("EmployeeCode", employeeCode);
            formData.append("FromDate", formatDateAPI(fromDate));   // use API format
            formData.append("ToDate", formatDateAPI(toDate));
            formData.append("NoOfDays", numberOfDays);
            formData.append("LeaveTypeID", leaveType);
            formData.append("Reason", comment);
            formData.append("Comment", comment);
            formData.append("Type", "APPLY");
            formData.append("EmployeeName", employeeName);

            // ✅ CONDITIONAL LOGIC
            if (leaveUnit === "FIRST_HALF" || leaveUnit === "SECOND_HALF") {
                formData.append("LeaveUnit", "HALFDAY"); // 🔥 fixed value
                formData.append("HalfDayType", leaveUnit); // FIRST_HALF / SECOND_HALF

            } else {
                formData.append("LeaveUnit", leaveUnit); // FULLDAY or others
            }
            const response = await LeaveProcessService.setEmployeeLeave(formData);
            debugger;
            if (response.success && response.data.statusCode === 200) {

                alert("Leave applied successfully");
                setFromDate(null);
                setToDate(null);
                setLeaveType("");
                setComment("");
                fetchMonthSummary(activeStartDate);
            } else {
                const message = response.errorMessage || 'Submission failed. Please try again.';
                alert(message);
            }

        } catch (error) {
            const message = error?.message || 'Unexpected error occurred.';
            alert(message);
            console.error("Unhandled error:", message);
        } finally {
            setLoading(false);

        }
    };

    // ================= UI =================

    return (

        <div className="container py-3">

            <div className="row">

                {/* CALENDAR */}

                <div className="col-md-4">

                    <Calendar
                        onClickDay={handleDateClick}
                        activeStartDate={activeStartDate}
                        maxDate={maxDate}
                        prev2Label="<<"
                        next2Label=">>"
                        onActiveStartDateChange={({ activeStartDate }) => {
                            setActiveStartDate(activeStartDate);
                        }}
                        tileClassName={({ date, view }) => {
                            if (view === "month") {

                                // Future dates
                                if (date > today) {
                                    return "future-date";
                                }

                                return getStatusColor(date);
                            }
                        }}
                    />
                    {/* Legend */} <div className="mt-3 text-white" > <span className="small-text"> <span className="small-text" style={{ background: "#28a745", padding: "5px 5px", marginRight: 0 }}>PP - Present</span> </span> <span > <span className="small-text" style={{ background: "#dc3545", padding: "5px 5px", marginRight: 5 }}>AA - Absent</span> </span> <span> <span className="small-text" style={{ background: "#ff9800", padding: "5px 5px", marginRight: 5 }}>AP/PA - Half Day</span> </span> <span> <span className="small-text" style={{ background: "#a7bdc7", padding: "5px 5px", marginRight: 5 }}>WO</span> </span><span> <span className="small-text" style={{ background: "purple", padding: "5px 5px", marginRight: 5 }}>HO</span> </span><span> <span className="small-text" style={{ background: "blue", padding: "5px 5px", marginRight: 5 }}>L</span> </span> </div>

                    <div className="card mt-3 p-3">

                        <h6 className="text-success">Leave Balance</h6>

                        {leaveTypes.length > 0 ? (

                            leaveTypes.map((leave, index) => (

                                <p key={index}>
                                    {leave.leaveCode} : <strong>{leave.availableBalance}</strong>
                                </p>

                            ))

                        ) : (

                            <p>No Leave Data</p>

                        )}

                    </div>

                </div>

                {/* APPLY LEAVE */}

                <div className="col-md-8">

                    <div className="card p-3">

                        {fromDate ? (

                            <>

                                <h5>
                                    Request from {formatDate(fromDate)} to {formatDate(toDate)}  Leave Apply
                                </h5>

                                <p><strong>No of days:</strong> {numberOfDays}</p>

                                {/* FROM DATE */}

                                <div className="mb-2">
                                    <label>From Date</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formatDate(fromDate)}
                                        readOnly
                                    />
                                </div>

                                {/* TO DATE */}

                                <div className="mb-2">
                                    <label>To Date</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formatDate(toDate)}
                                        readOnly
                                    />
                                </div>

                                {/* LEAVE UNIT */}

                                <div className="mb-3">

                                    <label>Leave Unit</label><br />

                                    <input
                                        type="radio"
                                        value="FULLDAY"
                                        checked={leaveUnit === "FULLDAY"}
                                        onChange={(e) => setLeaveUnit(e.target.value)}
                                    /> Full Day

                                    <input
                                        type="radio"
                                        value="FIRST_HALF"
                                        className="ms-3"
                                        checked={leaveUnit === "FIRST_HALF"}
                                        onChange={(e) => setLeaveUnit(e.target.value)}
                                    /> First Half

                                    <input
                                        type="radio"
                                        value="SECOND_HALF"
                                        className="ms-3"
                                        checked={leaveUnit === "SECOND_HALF"}
                                        onChange={(e) => setLeaveUnit(e.target.value)}
                                    /> Second Half

                                </div>

                                {/* LEAVE TYPE */}

                                <div className="mb-3">

                                    <label>Leave Type</label>

                                    <select
                                        className="form-select"
                                        value={leaveType}
                                        onChange={(e) => setLeaveType(e.target.value)}
                                    >

                                        <option value="">Select Leave</option>

                                        {leaveList.map((item, i) => (

                                            <option key={i} value={item.leaveTypeID}>
                                                {item.leaveName}
                                            </option>

                                        ))}

                                    </select>

                                </div>

                                {/* COMMENT */}

                                <div className="mb-3">

                                    <label>Comment</label>

                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    />

                                </div>

                                {/* SUBMIT */}

                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                >
                                    Submit Leave
                                </button>

                            </>

                        ) : (

                            <h6>Select date(s) from calendar for Leave Apply</h6>

                        )}

                    </div>

                </div>

            </div>

        </div>

    );
}